'use client';

import { useAuth } from '@/contexts/AuthProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';

/**
 * Hook to manage workspace-aware socket connections
 * Ensures socket connections are properly torn down and re-established during workspace switches
 */
export const useWorkspaceSocket = (socketInstance: Socket | null) => {
  const { activeWorkspace } = useWorkspace() || {};
  const { user } = useAuth();
  const previousWorkspaceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!socketInstance || !user || !activeWorkspace) return;

    const currentWorkspaceId = activeWorkspace.vendorid;
    const previousWorkspaceId = previousWorkspaceRef.current;

    // Only proceed if workspace actually changed
    if (previousWorkspaceId === currentWorkspaceId) return;

    console.log('🔄 Workspace socket: switching workspace', {
      from: previousWorkspaceId,
      to: currentWorkspaceId,
      userId: user.id,
    });

    // Step 1: Disconnect from all previous workspace rooms
    if (previousWorkspaceId !== null) {
      socketInstance.emit('leave_workspace_rooms', {
        workspaceId: previousWorkspaceId,
        userId: user.id,
      });
    }

    // Step 2: Connect to new workspace rooms
    const workspaceRoomId = `workspace_${currentWorkspaceId}_user_${user.id}`;
    socketInstance.emit('join_room', { room: workspaceRoomId });

    // Step 3: Update reference
    previousWorkspaceRef.current = currentWorkspaceId;

    // Cleanup function for component unmount
    return () => {
      if (socketInstance.connected) {
        socketInstance.emit('leave_workspace_rooms', {
          workspaceId: currentWorkspaceId,
          userId: user.id,
        });
      }
    };
  }, [socketInstance, activeWorkspace?.vendorid, user?.id]);

  // Listen for workspace switch events and handle disconnection
  useEffect(() => {
    const handleWorkspaceSwitch = (event: CustomEvent) => {
      const { oldWorkspaceId, newWorkspaceId } = event.detail;

      if (socketInstance && oldWorkspaceId) {
        console.log('🔄 Handling workspace switch event', { oldWorkspaceId, newWorkspaceId });
        socketInstance.emit('leave_workspace_rooms', {
          workspaceId: oldWorkspaceId,
          userId: user?.id,
        });
      }
    };

    window.addEventListener('workspaceSwitch', handleWorkspaceSwitch as EventListener);

    return () => {
      window.removeEventListener('workspaceSwitch', handleWorkspaceSwitch as EventListener);
    };
  }, [socketInstance, user?.id]);
};

export default useWorkspaceSocket;
